import { zodResolver } from "@hookform/resolvers/zod";
import type { ReferralLetterOptions } from "@rosc/contracts";
import { isDictatedReport } from "@rosc/contracts";
import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { AppConfig } from "@/common/appConfig";
import { Layout } from "@/components/layout";
import { DictationReportV32 } from "@/components/ui/dictation/DictationReportV32";
import { LoadErrorBanner } from "@/components/ui/redesign/page";
import { ReferralLetterDialog } from "@/components/ui/referral/ReferralLetterDialog";
import { DocumentGenerationProgress } from "@/components/ui/visits/DocumentGenerationProgress";
import { MultiRecordingSidebar } from "@/components/ui/visits/multi-recording-sidebar";
import { NoteGenerationNotice } from "@/components/ui/visits/NoteGenerationNotice";
import { RecordingInstructionsNotice } from "@/components/ui/visits/RecordingInstructionsNotice";
import { VisitErrorNotice } from "@/components/ui/visits/VisitErrorNotice";
import { toast } from "@/hooks/use-toast";
import { useVisitActions } from "@/hooks/useVisitActions";
import { useNoteGeneration } from "@/hooks/visits/useNoteGeneration";
import { useVisitReads, VisitReadsProvider } from "@/hooks/visits/VisitReads";
import { VisitSpeakersProvider } from "@/hooks/visits/VisitSpeakers";
import { isPreChartDraft } from "@/lib/pre-chart";
import type { ReadingLevel } from "@/lib/reading-level";
import type { SummaryLanguage } from "@/lib/summary-language";
import { getFormattedDateToHour } from "@/lib/time";
import { getVisitTitle } from "@/lib/utils";
import { useVisitScope } from "@/lib/visit-queries";
import { Route } from "@/routes/visits/$visitPublicId";
import { EditTitleVisitSchema } from "@/schemas/editTitleVisit.schema";
import { usePreChartStore } from "@/store/preChart.store";
import { EditTitleVisitDialogForm } from "./components/EditTitleVisitDialogForm";
import { VisitUnavailable } from "./components/VisitUnavailable";
import { VisitWorkspace } from "./workspace/VisitWorkspace";

const VisitContent = ({
  visitPublicId,
}: {
  visitPublicId: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isMultiRecordingOpen, setIsMultiRecordingOpen] = useState(false);
  // "Start visit" on a pre-chart draft opens the same recorder as Add
  // recording, titled Start visit, and starts recording at once.
  const [recordingMode, setRecordingMode] = useState<"add" | "startVisit">(
    "add",
  );
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const {
    file,
    notes,
    loading,
    error,
    unavailable,
    relatedError,
    multiRecordingFiles,
    refresh,
  } = useVisitReads();
  // Deleted, another account's or a malformed link: nothing to show or retry.
  const gone = unavailable && !file && !loading;
  const visitTitle = file
    ? getVisitTitle(file.title, file.rowno)
    : t("visit.title");
  const refreshVisit = useCallback(() => {
    void refresh();
  }, [refresh]);
  const speakerTranscripts = useMemo(
    () => [
      ...(file
        ? [{ transcription: file.transcription, status: file.status }]
        : []),
      ...multiRecordingFiles.map((recording) => ({
        transcription: recording.transcription,
        status: recording.status,
      })),
    ],
    [file, multiRecordingFiles],
  );

  const {
    changeTitleVisitByFile,
    deleteVisitByFile,
    result: changeTitleResult,
    loading: changeTitleLoading,
    error: changeTitleError,
  } = useVisitActions();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const draft = isPreChartDraft(file?.status);
  const startVisit = useCallback(() => {
    setRecordingMode("startVisit");
    setIsMultiRecordingOpen(true);
  }, []);
  // "Start visit" from the visits list: start once the draft has loaded.
  useEffect(() => {
    if (draft && usePreChartStore.getState().takeStartVisit(visitPublicId))
      startVisit();
  }, [draft, visitPublicId, startVisit]);

  const generation = useNoteGeneration({
    visitPublicId: file?.public_id,
    open: true,
  });

  const form = useForm({
    resolver: zodResolver(EditTitleVisitSchema),
    defaultValues: {
      title: `${t("visit.title")}`,
    },
  });

  useEffect(() => {
    if (file) {
      const title = getVisitTitle(file.title, file.rowno);
      if (!showEditTitleModal) {
        form.reset({
          title: title,
        });
      }
    }
  }, [file, form, t, showEditTitleModal]);

  const toggleExpanded = (id: string): void => {
    setExpandedItems((previous) =>
      previous.includes(id)
        ? previous.filter((x) => x !== id)
        : [...previous, id],
    );
  };

  useEffect(() => {
    if (!changeTitleLoading) {
      if (changeTitleResult) {
        toast({
          variant: "success",
          title: changeTitleResult,
        });
        setShowEditTitleModal(false);
      }
      if (changeTitleError) {
        toast({
          variant: "error",
          title: changeTitleError,
        });
      }
    }
  }, [changeTitleResult, changeTitleError, changeTitleLoading]);

  const handleSubmit = (data: z.infer<typeof EditTitleVisitSchema>): void => {
    if (file) {
      void changeTitleVisitByFile(file, data.title);
    }
  };

  const acceptNote = async (operation: Promise<boolean>): Promise<void> => {
    if ((await operation) && generation.isCurrent()) {
      toast({
        variant: "success",
        title: t("visit.messages.createSuccessful"),
      });
    }
  };
  // VITE_REFERRAL_TEMPLATES_ENABLED: Create → Referral letter first asks for
  // the specialty, recipient, instructions and template.
  const [referralSources, setReferralSources] = useState<
    readonly number[] | null
  >(null);
  // The document being written (Create → Referral letter or Patient
  // summary), for its progress above the documents; kept for a retry.
  const [writing, setWriting] = useState<
    "Referral Letter" | "Patient Summary" | null
  >(null);
  const generateNote = async (
    template: "Referral Letter" | "Patient Summary",
    sourceIds: readonly number[] = [],
    summary?: { readingLevel: ReadingLevel; language: SummaryLanguage },
    referral?: ReferralLetterOptions,
  ): Promise<void> => {
    if (
      !file ||
      generation.loading ||
      generation.recovery ||
      !generation.beginDraft()
    )
      return;
    setWriting(template);
    await acceptNote(
      generation.submit({
        template,
        style:
          file.style ||
          (template === "Referral Letter" ? "Paragraph" : "Bullet Point"),
        format: file.format || "Concise",
        exam: file.exam || "",
        macroPublicIds: [],
        customTemplatePublicId: null,
        ...(sourceIds.length ? { sourceIds: [...sourceIds] } : {}),
        ...(template === "Patient Summary" && summary
          ? {
              readingLevel: summary.readingLevel,
              summaryLanguage: summary.language,
            }
          : {}),
        ...(template === "Referral Letter" && referral ? { referral } : {}),
      }),
    );
  };

  const notices = (
    <>
      {(error || relatedError) && (
        <LoadErrorBanner
          description={t("visit.loadError.description")}
          title={unavailable ? t("visit.unavailable") : t("error.unknown")}
          onRetry={refreshVisit}
        />
      )}
      <VisitErrorNotice
        status={file?.status}
        visitPublicId={file?.public_id}
        onAddRecording={
          AppConfig.enableMultirecording
            ? () => {
                setRecordingMode("add");
                setIsMultiRecordingOpen(true);
              }
            : undefined
        }
      />
      {/* Only this page's own request (staging check 04/11: Create → New
          note, written in the sheet, showed the patient summary's card
          here). */}
      {generation.submitting && writing && (
        <DocumentGenerationProgress kind={writing} />
      )}
      <NoteGenerationNotice
        error={generation.error}
        recovery={generation.recovery}
        loading={generation.loading}
        submitting={generation.submitting}
        quietWhileRunning
        onRetry={() => acceptNote(generation.retry())}
        onAbandon={generation.abandon}
      />
      <RecordingInstructionsNotice
        visitPublicId={file?.public_id}
        visitStatus={file?.status}
        onNoteChanged={refreshVisit}
      />
    </>
  );

  // A dictated report opens back into the Dictated report screen, to
  // continue dictating, copy it or switch its template.
  if (file && isDictatedReport(file.status))
    return (
      <Layout
        isLoading={false}
        navbarTitle={t("dictationReport.title")}
        topBar={AppConfig.enableAppRedesign ? "none" : undefined}
        breadcrumbs={[
          { name: t("visits.title"), href: `/visits`, current: false },
        ]}
      >
        <div
          className="flex min-h-0 flex-1 flex-col"
          data-testid="visit-dictated-report"
        >
          <DictationReportV32
            saved={{
              public_id: String(file.public_id),
              title: file.title,
              result: file.result,
              transcription: file.transcription,
              customTemplatePublicId: file.customTemplatePublicId,
            }}
            onClose={() => void navigate({ to: "/visits" })}
            onDeleted={() => void navigate({ to: "/visits" })}
            onOpenReport={(id) =>
              void navigate({
                to: "/visits/$visitPublicId",
                params: { visitPublicId: id },
              })
            }
          />
        </div>
      </Layout>
    );

  return (
    <VisitSpeakersProvider
      refresh={refresh}
      transcripts={speakerTranscripts}
      visitPublicId={file?.public_id ?? null}
    >
      <Layout
        isLoading={loading}
        navbarTitle={t("visit.title")}
        // v3.2: the workspace draws its own top bar.
        topBar={AppConfig.enableAppRedesign ? "none" : undefined}
        breadcrumbs={[
          { name: t("visits.title"), href: `/visits`, current: false },
          ...(file?.rowno
            ? [
                {
                  name: visitTitle,
                  href: `/visits/${visitPublicId}`,
                  current: true,
                },
              ]
            : []),
        ]}
      >
        {gone ? (
          <VisitUnavailable />
        ) : (
          <VisitWorkspace
            created={file ? getFormattedDateToHour(file.createDate) : null}
            expandedItems={expandedItems}
            file={file}
            multiRecordingFiles={multiRecordingFiles}
            noteGenerationBlocked={generation.loading || generation.recovery}
            notes={notes}
            notices={notices}
            refresh={refreshVisit}
            title={visitTitle}
            toggleExpanded={toggleExpanded}
            onAddRecording={
              AppConfig.enableMultirecording
                ? () => {
                    setRecordingMode("add");
                    setIsMultiRecordingOpen(true);
                  }
                : undefined
            }
            onStartVisit={startVisit}
            onSaveDraft={() => {
              toast({
                variant: "success",
                title: t("preChart.savedToast"),
              });
              void navigate({ to: "/visits" });
            }}
            onEditTitleClick={() => {
              setShowEditTitleModal(true);
            }}
            deleting={deleting}
            onDelete={
              file
                ? () => {
                    setDeleting(true);
                    void deleteVisitByFile(file)
                      .then((deleted) => {
                        if (deleted) void navigate({ to: "/visits" });
                      })
                      .finally(() => {
                        setDeleting(false);
                      });
                  }
                : undefined
            }
            onRename={
              file ? (next) => changeTitleVisitByFile(file, next) : undefined
            }
            onGeneratePatientSummary={(sourceIds, readingLevel, language) => {
              void generateNote("Patient Summary", sourceIds, {
                readingLevel,
                language,
              });
            }}
            onGenerateReferralLetter={(sourceIds) => {
              if (AppConfig.enableReferralTemplates)
                setReferralSources(sourceIds ?? []);
              else void generateNote("Referral Letter", sourceIds);
            }}
          />
        )}
        {AppConfig.enableReferralTemplates && (
          <ReferralLetterDialog
            open={referralSources !== null}
            onGenerate={(referral) => {
              void generateNote(
                "Referral Letter",
                referralSources ?? [],
                undefined,
                referral,
              );
            }}
            onOpenChange={(open) => {
              if (!open) setReferralSources(null);
            }}
          />
        )}
        <MultiRecordingSidebar
          mode={recordingMode}
          open={isMultiRecordingOpen}
          onOpenChange={setIsMultiRecordingOpen}
        />
        <FormProvider {...form}>
          <EditTitleVisitDialogForm
            loading={changeTitleLoading}
            open={showEditTitleModal}
            onOpenChange={setShowEditTitleModal}
            onSubmit={handleSubmit}
          />
        </FormProvider>
      </Layout>
    </VisitSpeakersProvider>
  );
};

export const Visit = (): JSX.Element => {
  const { useParams } = Route;
  const { visitPublicId: routeVisitPublicId } = useParams();
  const visitPublicId = routeVisitPublicId.toLowerCase();
  const scope = useVisitScope();
  return (
    <VisitReadsProvider
      key={`${scope?.uid}:${scope?.accountPublicId}:${scope?.generation}:${visitPublicId}`}
      id={visitPublicId}
    >
      <VisitContent visitPublicId={visitPublicId} />
    </VisitReadsProvider>
  );
};
